import React, { useState } from 'react';
import { Trash2, Loader2, ChevronDown } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { useToast } from '@/shared/hooks/use-toast';
import {
  useCreateManpowerEntry,
  useDeleteAttachment,
  useDeleteManpowerEntry,
  useUploadAttachments,
} from '../hooks';
import type { DailyLog, ManpowerEntry, ManpowerEntryDraft } from '../types';
import { AttachmentsCell } from './AttachmentsCell';

interface ManpowerSectionProps {
  projectId: number;
  dailyLog: DailyLog;
  expanded: boolean;
  onToggle: () => void;
}

const blankDraft = (): ManpowerEntryDraft => ({
  company: '',
  workers: 1,
  hours: 8,
  location: '',
  comments: '',
});

const fmtNumber = (value: number | string): string => {
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '0';
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

export const ManpowerSection: React.FC<ManpowerSectionProps> = ({
  projectId,
  dailyLog,
  expanded,
  onToggle,
}) => {
  const { toast } = useToast();
  const [draft, setDraft] = useState<ManpowerEntryDraft>(blankDraft);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const createMutation = useCreateManpowerEntry();
  const deleteMutation = useDeleteManpowerEntry();
  const uploadMutation = useUploadAttachments(projectId);
  const deleteAttachmentMutation = useDeleteAttachment();

  const isCreating = createMutation.isPending || uploadMutation.isPending;

  const handleCreate = async () => {
    if (!draft.company.trim()) {
      toast({
        title: 'Company required',
        description: 'Please enter a company before saving the row.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const created = await createMutation.mutateAsync({
        dailyLogId: dailyLog.id,
        draft: { ...draft, company: draft.company.trim() },
      });
      if (pendingFiles.length > 0) {
        await uploadMutation.mutateAsync({
          files: pendingFiles,
          parent: { kind: 'manpower', id: created.id },
        });
      }
      setDraft(blankDraft());
      setPendingFiles([]);
    } catch (error) {
      toast({
        title: 'Failed to create entry',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (entry: ManpowerEntry) => {
    const confirmed = window.confirm(`Remove ${entry.company}'s row?`);
    if (!confirmed) return;
    try {
      await deleteMutation.mutateAsync(entry.id);
    } catch (error) {
      toast({
        title: 'Failed to delete entry',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleUploadToExisting = async (entry: ManpowerEntry, files: File[]) => {
    try {
      await uploadMutation.mutateAsync({
        files,
        parent: { kind: 'manpower', id: entry.id },
      });
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <section className="rounded-md border bg-card">
      <header
        className="flex cursor-pointer items-center justify-between px-4 py-3"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${
              expanded ? '' : '-rotate-90'
            }`}
          />
          <h2 className="text-base font-semibold">Manpower</h2>
        </div>
        <div className="text-xs text-muted-foreground">
          {dailyLog.total_workers} Worker{dailyLog.total_workers === 1 ? '' : 's'}
          {' | '}
          {fmtNumber(dailyLog.total_hours)} Total Hours
        </div>
      </header>

      {expanded && (
        <div className="overflow-x-auto border-t">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[180px]">Company</TableHead>
                <TableHead className="w-[110px]">
                  Workers <span className="text-destructive">*</span>
                </TableHead>
                <TableHead className="w-[110px]">
                  Hours <span className="text-destructive">*</span>
                </TableHead>
                <TableHead className="w-[110px]">Total Hours</TableHead>
                <TableHead className="min-w-[160px]">Location</TableHead>
                <TableHead className="min-w-[200px]">Comments</TableHead>
                <TableHead className="min-w-[220px]">Attachments</TableHead>
                <TableHead className="min-w-[140px]">Created By</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {dailyLog.manpower_entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="font-medium">{entry.company}</TableCell>
                  <TableCell>{entry.workers}</TableCell>
                  <TableCell>{fmtNumber(entry.hours)}</TableCell>
                  <TableCell className="font-medium">{fmtNumber(entry.total_hours)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {entry.location || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {entry.comments || '—'}
                  </TableCell>
                  <TableCell>
                    <AttachmentsCell
                      attachments={entry.attachments}
                      isUploading={uploadMutation.isPending}
                      onAttach={(files) => handleUploadToExisting(entry, files)}
                      onRemoveExisting={(uuid) =>
                        deleteAttachmentMutation.mutate(uuid)
                      }
                    />
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {entry.created_by
                      ? `${entry.created_by.first_name || ''} ${entry.created_by.last_name || ''}`.trim() ||
                        entry.created_by.email
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(entry)}
                      aria-label="Delete row"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

              {/* Draft row */}
              <TableRow className="bg-muted/20 hover:bg-muted/20">
                <TableCell>
                  <Input
                    placeholder="Search"
                    value={draft.company}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, company: event.target.value }))
                    }
                    className="h-9"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={draft.workers}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        workers: Math.max(1, Number(event.target.value) || 1),
                      }))
                    }
                    className="h-9 w-20"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    min={0}
                    step={0.25}
                    value={draft.hours}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        hours: Math.max(0, Number(event.target.value) || 0),
                      }))
                    }
                    className="h-9 w-20"
                  />
                </TableCell>
                <TableCell className="font-medium text-muted-foreground">
                  {fmtNumber((draft.workers || 0) * (draft.hours || 0))}
                </TableCell>
                <TableCell>
                  <Input
                    placeholder="Select a Location"
                    value={draft.location}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, location: event.target.value }))
                    }
                    className="h-9"
                  />
                </TableCell>
                <TableCell>
                  <Textarea
                    placeholder="Add comment"
                    value={draft.comments}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, comments: event.target.value }))
                    }
                    className="min-h-[36px] resize-none py-1.5"
                    rows={1}
                  />
                </TableCell>
                <TableCell>
                  <AttachmentsCell
                    attachments={[]}
                    pendingFiles={pendingFiles}
                    onAttach={(files) =>
                      setPendingFiles((prev) => [...prev, ...files])
                    }
                    onRemovePending={(index) =>
                      setPendingFiles((prev) => prev.filter((_, i) => i !== index))
                    }
                  />
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">—</TableCell>
                <TableCell>
                  <Button
                    type="button"
                    onClick={handleCreate}
                    disabled={isCreating}
                    className="h-9"
                  >
                    {isCreating && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                    Create
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
};
